import { Card, ErrorView, LoadingView, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Button, Label, Textbox } from '@app/engine';
import { Logger } from '@shared/common';
import { KBClient, Telemetry } from '@shared/frontend';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { SyntheticEvent } from 'react';

/** File-local constants. */
const localConstants = {
  logContext: 'KnowledgeEdit'
} as const;

/**
 * Create or edit a knowledge document. A `documentID` route param puts the page in edit mode: the existing title and body are loaded (title from the document list, body from the gated content
 * endpoint) and a save issues an update; otherwise a save creates a new document. Every document body is plain text and flows through the same chunk/embed ingestion, so the content is searchable
 * once ingestion completes.
 *
 * @returns The knowledge create/edit screen element.
 */
export function KnowledgeEdit() {
  const { tenantID } = useDashboard();
  const params = useParams();
  const navigate = useNavigate();
  const documentID = params[Constants.Param.DocumentID] ?? '';
  const isEdit = documentID.length > 0;

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);

  // In edit mode, load the existing title (from the document list) and body (from the gated content endpoint) so the form is pre-filled. A missing document bounces back to the list.
  useEffect(() => {
    if (!isEdit || !tenantID) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    void (async () => {
      try {
        const [documents, blob] = await Promise.all([KBClient.ListDocuments(controller.signal, tenantID), KBClient.GetDocumentContent(controller.signal, tenantID, documentID)]);
        const document = documents.find((doc) => doc.id === documentID);
        if (!document) {
          navigate(Constants.Route.Knowledge);
          return;
        }
        setName(document.title);
        setBody(await blob.text());
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        setLoadError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [isEdit, tenantID, documentID, navigate]);

  const onSave = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!tenantID || saving) return;
    if (body.trim().length === 0) {
      setFeedback({ text: Localized.KnowledgeBodyPlaceholder, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      if (isEdit) await KBClient.UpdateManualDocument(controller.signal, tenantID, documentID, name, body);
      else await KBClient.CreateManualDocument(controller.signal, tenantID, name, body);
      Telemetry.KnowledgeDocumentEdited(!isEdit);
      navigate(Constants.Route.Knowledge);
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: isEdit ? Localized.KnowledgeUpdateError : Localized.KnowledgeCreateError, type: 'error' });
      setSaving(false);
    }
  };

  const title = isEdit ? Localized.KnowledgeEditTitle : Localized.KnowledgeNewTitle;

  return (
    <div className={Styles.cardStack}>
      <Card title={title} feedback={<FeedbackMessage feedback={feedback} />}>
        {loading ? (
          <LoadingView className={Styles.panelLoading} />
        ) : loadError ? (
          <ErrorView onRetry={() => navigate(Constants.Route.Knowledge)} />
        ) : (
          <form onSubmit={onSave}>
            <div className={Styles.formGrid}>
              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <Label classNameContainer={Styles.label} text={Localized.KnowledgeName} title={Localized.KnowledgeName} />
                <Textbox value={name} placeholder={Localized.KnowledgeNamePlaceholder} onChange={setName} classNameText={Styles.input} />
              </div>

              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <Label classNameContainer={Styles.label} text={Localized.KnowledgeBody} title={Localized.KnowledgeBody} />
                <Textbox multiline value={body} placeholder={Localized.KnowledgeBodyPlaceholder} onChange={setBody} classNameText={`${Styles.textarea} ${Styles.textareaTall}`} />
              </div>
            </div>

            <div className={Styles.formActions}>
              <Button
                type="button"
                text={Localized.SharedCancel}
                disabled={saving}
                classNameButtonEnabled={`${Styles.btn}`}
                classNameButtonDisabled={`${Styles.btn}`}
                onClickHandler={() => navigate(Constants.Route.Knowledge)}
              />
              <Button
                text={saving ? Localized.KnowledgeSaving : Localized.KnowledgeSave}
                disabled={saving}
                classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`}
                classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary}`}
              />
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
